import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { X, Banknote, Building2, Phone, Mail, MapPin, Clock, Loader2 } from 'lucide-react';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

function CashModalContent({ onClose, onConfirm, totalAmount, isLoading }) {
  return (
    <div className="flex items-center justify-center p-4 h-full">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Banknote className="w-5 h-5 text-[#589DD5]" />
            <h2 className="text-lg font-semibold">Pay with Cash</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          {/* Amount */}
          <div className="bg-[#589DD5]/10 dark:bg-[#589DD5]/20 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">Amount to Pay</p>
            <p className="text-2xl font-bold text-[#589DD5]">${totalAmount.toFixed(2)}</p>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-white">Instructions</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Please visit our office during business hours to complete your cash payment. Bring exact change if possible.
            </p>
          </div>

          {/* Office Info */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#589DD5]" />
              Office Information
            </h4>

            <div className="space-y-2 text-sm">
              <a
                href="https://maps.app.goo.gl/s6rukqMufxCzbBdn9"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-gray-600 dark:text-gray-400 hover:text-[#589DD5] dark:hover:text-[#589DD5] transition-colors"
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>65 Challenger Rd Suite 201, Ridgefield Park, NJ 07660</span>
              </a>

              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>201-266-8882 / 201-346-5689</span>
              </div>

              <a
                href="mailto:aoneinstituteoffice@gmail.com"
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#589DD5] dark:hover:text-[#589DD5] transition-colors"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>aoneinstituteoffice@gmail.com</span>
              </a>

              <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p>Mon - Fri: 2:00 PM - 10:00 PM (ET)</p>
                  <p>Sat: 10:00 AM - 6:30 PM (ET)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Note:</strong> Your enrollment will be confirmed once our office receives and verifies the cash payment.
            </p>
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-2.5 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              'I wish to pay with Cash'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CashModal({ isOpen, onClose, onConfirm, totalAmount, isLoading, zIndex = 200 }) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex }}>
        <CashModalContent
          onClose={onClose}
          onConfirm={onConfirm}
          totalAmount={totalAmount}
          isLoading={isLoading}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
